import React, {useState} from "react";
import InputBase from "../InputBase";
import PropTypes from "prop-types";
import {useTheme} from "../../../../theme/ThemeProvider";
import DropDownSelect from "../../dropdown/DropDownSelect";
import FlexContainer from "../../../layout/FlexContainer";
import Text from "../../../display/Text";
import Container from "connectui/component/layout/Container";

/**
 * TimeInput is a component that allows users to manually input a time in text format.
 * It automatically adds the colon visually, highlights invalid values, and provides an AM/PM selector.
 * The time is converted to 24-hour format before being passed to onChange.
 *
 * @param {Object} props - The props for the TimeInput component.
 * @param {Object} [props.inputProps={}] - The properties to pass to the `<input>` element.
 * @param {React.ReactNode} [props.startIcon=null] - The icon to display before the input field.
 * @param {React.ElementType} [props.inputBase=InputBase] - The component to render for the input base (defaults to InputBase).
 * @param {function} onChange - Callback function that receives the formatted 24-hour time string.
 * @param {Object} [props.sx={}] - The style object that can be used to override default input styles.
 * @param {Object} props.rest - Any other props that should be passed to the InputBase component.
 *
 * @returns {JSX.Element} The rendered TimeInput component.
 */
const TimeInput = ({
  inputProps = {},
  startIcon = null,
  inputBase: InputBaseComponent = InputBase,
  sx = {},
  onChange,
  ...props
}) => {
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    return {
      time: `${String(hours % 12 || 12).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
      period: hours >= 12 ? "PM" : "AM",
    };
  };

  const {theme} = useTheme();
  const [inputValue, setInputValue] = useState(getCurrentTime().time);
  const [period, setPeriod] = useState(getCurrentTime().period);
  const [invalid, setInvalid] = useState(false);

  const handleChange = (e) => {
    let rawValue = e.target.value.replace(/[^0-9]/g, "");
    if (rawValue.length > 4) return;

    let formattedValue = rawValue;
    if (rawValue.length >= 3) {
      formattedValue = rawValue.slice(0, 2) + ":" + rawValue.slice(2);
    }

    const [hours, minutes] = formattedValue
      .split(":")
      .map((num) => parseInt(num, 10) || 0);
    let isValid = hours >= 1 && hours <= 12 && minutes >= 0 && minutes <= 59;
    if (formattedValue.length < 5) isValid = false;
    setInvalid(!isValid);
    setInputValue(formattedValue);

    if (isValid) {
      const convertedHours = period === "PM" ? (hours % 12) + 12 : hours % 12;
      const formattedTime = `${String(convertedHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      onChange(formattedTime);
    }
  };

  return (
    <InputBaseComponent
      inputProps={{
        type: "text",
        value: inputValue,
        onChange: handleChange,
        placeholder: "HH:MM",
        maxLength: 5,
        style: {flex: 1},
        ...inputProps,
      }}
      startIcon={startIcon}
      sx={{
        border: invalid ? "1px solid red" : "1px solid " + theme.colors.gray300,
        ...sx,
      }}
      actions={
        <Container>
          <DropDownSelect
            onChange={(e) => {
              setPeriod(e);
              const [hours, minutes] = inputValue
                .split(":")
                .map((num) => parseInt(num, 10) || 0);
              if (!isNaN(hours) && !isNaN(minutes)) {
                const convertedHours =
                  e === "PM" ? (hours % 12) + 12 : hours % 12;
                const formattedTime = `${String(convertedHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
                onChange(formattedTime);
              }
            }}
            label={
              <FlexContainer alignItems="center" gap={theme.spacing(1.5)}>
                <Text variant="textXS" weight="medium" color="gray700">
                  {period}
                </Text>
              </FlexContainer>
            }
          >
            <FlexContainer
              key={"AM"}
              value={"AM"}
              alignItems="center"
              gap={theme.spacing(2)}
              sx={{cursor: "pointer"}}
            >
              <Text variant="textMD" weight="medium">
                AM
              </Text>
            </FlexContainer>
            <FlexContainer
              key={"PM"}
              value={"PM"}
              alignItems="center"
              gap={theme.spacing(2)}
              sx={{cursor: "pointer"}}
            >
              <Text variant="textMD" weight="medium">
                PM
              </Text>
            </FlexContainer>
          </DropDownSelect>
        </Container>
      }
    ></InputBaseComponent>
  );
};

TimeInput.propTypes = {
  inputProps: PropTypes.object,
  startIcon: PropTypes.node,
  inputBase: PropTypes.any,
  sx: PropTypes.any,
  onChange: PropTypes.func.isRequired,
};

export default TimeInput;
